import { useState, useRef, useCallback, useEffect } from 'react'
import { useStore }      from '@/store/StoreContext'
import { useT, useLang } from '@/i18n/I18nContext'
import { useAuth }       from '@/auth/AuthContext'

const API = import.meta.env.VITE_API_URL || 'http://localhost:5001/api'

const CATEGORIES = [
  { id: "animals",    label: "Animals",         icon: "🐘" },
  { id: "fruits",     label: "Fruits",           icon: "🍎" },
  { id: "vegetables", label: "Vegetables",       icon: "🥕" },
  { id: "transport",  label: "Transport",        icon: "🚌" },
  { id: "body",       label: "Body Parts",       icon: "👁️" },
  { id: "colors",     label: "Colors & Shapes",  icon: "🔴" },
  { id: "numbers",    label: "Numbers",          icon: "🔢" },
  { id: "household",  label: "Household",        icon: "🏠" },
  { id: "nature",     label: "Nature",           icon: "🌿" },
  { id: "people",     label: "People & Family",  icon: "👨‍👩‍👧" },
  { id: "actions",    label: "Actions / Verbs",  icon: "🏃" },
  { id: "emotions",   label: "Emotions",         icon: "😊" },
  { id: "custom",     label: "Custom",           icon: "✨" },
]

const LANGUAGES = [
  { code: "en", name: "English" },
  { code: "ml", name: "മലയാളം" },
  { code: "hi", name: "हिंदी"   },
  { code: "ta", name: "தமிழ்"  },
  { code: "te", name: "తెలుగు" },
]

const MODULES = [
  { id: "flashcard",  label: "Flashcard",      icon: "🃏" },
  { id: "worksheet",  label: "Worksheet",      icon: "📝" },
  { id: "activity",   label: "Activity Card",  icon: "🖼️" },
  { id: "storybook",  label: "Story Book",     icon: "📖" },
  { id: "workplan",   label: "Work Plan",      icon: "📅" },
  { id: "readiness",  label: "Readiness Test", icon: "🎮" },
]

const SORT_OPTIONS = [
  { id: "name-asc",  label: "Name A–Z"     },
  { id: "name-desc", label: "Name Z–A"     },
  { id: "date-desc", label: "Newest First" },
  { id: "date-asc",  label: "Oldest First" },
  { id: "category",  label: "By Category"  },
]

// Convert server Image doc → store-compatible shape
function serverDocToItem(doc) {
  const base = (import.meta.env.VITE_API_URL || 'http://localhost:5001/api').replace('/api', '')
  const resolveUrl = (u) => (!u ? '' : u.startsWith('http') ? u : `${base}${u}`)
  return {
    id:        doc._id,
    url:       resolveUrl(doc.url),
    thumbnail: resolveUrl(doc.thumbnail || doc.url),
    category:  doc.category || 'custom',
    labels:    doc.labels   || { en: doc.title || '' },
    tags:      doc.tags     || [],
    modules:   [],
    indicators: doc.indicators || [],
    domains:    doc.domains    || [],
    addedAt:   doc.createdAt,
    source:    'upload',
    filename:  doc.originalName,
    size:      formatBytes(doc.size),
    serverId:    doc._id,
    uploadedBy:  doc.uploadedBy?._id || doc.uploadedBy || null,
    uploaderName:doc.uploadedBy?.name || null,
    title:       doc.title || '',
  }
}

function formatBytes(bytes) {
  if (!bytes) return ''
  const kb = bytes / 1024
  return kb > 1024 ? `${(kb / 1024).toFixed(1)} MB` : `${Math.round(kb)} KB`
}

// Best display name for an item — title > labels.en > filename > 'Image'
function itemName(it) {
  return it?.labels?.en || it?.title || it?.filename?.replace(/\.[^.]+$/, '') || 'Image'
}

// Has this image been enriched with NCF development indicators yet?
// Tags are just free-text search keywords, not part of the taxonomy, so
// they don't factor into "Pending" — only indicators do.
function metaStatus(item) {
  const pending = !item.indicators?.length
  return { pending, missing: pending ? ['Indicators'] : [] }
}

// ── UploadZone ────────────────────────────────────────────────────────────────
function UploadZone({ getAuthHeader, onUpload, onGoToLibrary }) {
  const fileRef = useRef()
  const [dragging,  setDragging]  = useState(false)
  const [uploading, setUploading] = useState(false)
  const [uploadLog, setUploadLog] = useState([])
  const [done,      setDone]      = useState(false)

  const uploadFiles = useCallback(async (files) => {
    const imgs = Array.from(files).filter(f => f.type.startsWith('image/'))
    if (!imgs.length) return

    setUploading(true)
    setUploadLog(imgs.map(f => ({ name: f.name, status: 'uploading' })))

    try {
      if (imgs.length === 1) {
        const fd = new FormData()
        fd.append('image', imgs[0])
        fd.append('title', imgs[0].name.replace(/\.[^.]+$/, '').replace(/[-_]/g, ' '))
        fd.append('category', 'custom')

        const res  = await fetch(`${API}/collection/upload`, {
          method:  'POST',
          headers: { Authorization: getAuthHeader().Authorization },
          body:    fd,
        })
        const data = await res.json()
        if (!data.success) throw new Error(data.message)

        setUploadLog([{ name: imgs[0].name, status: 'done' }])
        onUpload(serverDocToItem(data.image))
        setDone(true)

      } else {
        const batch = imgs.slice(0, 20)
        const fd = new FormData()
        batch.forEach(f => fd.append('images', f))
        fd.append('category', 'custom')

        const res  = await fetch(`${API}/collection/bulk`, {
          method:  'POST',
          headers: { Authorization: getAuthHeader().Authorization },
          body:    fd,
        })
        const data = await res.json()
        if (!data.success) throw new Error(data.message)

        setUploadLog(batch.map(f => ({ name: f.name, status: 'done' })))
        data.images.forEach(doc => onUpload(serverDocToItem(doc)))
        setDone(true)
      }
    } catch (err) {
      setUploadLog(imgs.map(f => ({ name: f.name, status: 'error', error: err.message })))
    }

    setUploading(false)
  }, [getAuthHeader, onUpload])

  return (
    <div>
      <div
        onClick={() => !uploading && fileRef.current?.click()}
        onDrop={e => { e.preventDefault(); setDragging(false); uploadFiles(e.dataTransfer.files) }}
        onDragOver={e => { e.preventDefault(); setDragging(true) }}
        onDragLeave={() => setDragging(false)}
        style={{ border:`2.5px dashed ${dragging ? '#003366' : '#aabbd6'}`, borderRadius:14, padding:'28px 20px', textAlign:'center', cursor: uploading ? 'wait' : 'pointer', background: dragging ? '#e8f0fe' : 'white', transition:'all .2s', marginBottom:20 }}
      >
        <div style={{ fontSize:36, marginBottom:8 }}>{uploading ? '⏳' : '📁'}</div>
        <div style={{ fontWeight:800, color:'#003366', fontSize:15, marginBottom:4 }}>
          {uploading ? 'Uploading to server…' : 'Upload Images'}
        </div>
        <div style={{ fontSize:12, color:'#888' }}>Drag & drop or click · PNG, JPG, WebP, SVG · max 20 at once</div>
        <input ref={fileRef} type="file" multiple accept="image/*" style={{ display:'none' }} onChange={e => uploadFiles(e.target.files)}/>
      </div>

      {uploadLog.length > 0 && (
        <div style={{ background:'white', borderRadius:12, padding:16, marginBottom:20, boxShadow:'0 1px 4px rgba(0,0,0,.06)' }}>
          <div style={{ fontWeight:700, fontSize:12, color:'#003366', marginBottom:10 }}>Upload Status</div>
          {uploadLog.map((f, i) => (
            <div key={i} style={{ display:'flex', alignItems:'center', gap:10, padding:'6px 0', borderBottom: i < uploadLog.length - 1 ? '1px solid #f0f4f8' : 'none' }}>
              <span style={{ fontSize:14 }}>
                {f.status === 'done' ? '✅' : f.status === 'error' ? '❌' : '⏳'}
              </span>
              <span style={{ flex:1, fontSize:12, color:'#333', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{f.name}</span>
              {f.error && <span style={{ fontSize:11, color:'#c0392b' }}>{f.error}</span>}
            </div>
          ))}
          {done && (
            <button onClick={onGoToLibrary} style={{ marginTop:14, width:'100%', padding:'10px', background:'#003366', color:'white', border:'none', borderRadius:8, fontWeight:700, fontSize:13, cursor:'pointer' }}>
              → View in Library
            </button>
          )}
        </div>
      )}
    </div>
  )
}

// ── IndicatorTagPicker ─────────────────────────────────────────────────────────
// Shared chip-picker for the 40 NCF indicators, grouped by domain.
// Used by EditModal (per-image) and the bulk-tag toolbar.
function IndicatorTagPicker({ taxonomy, selected, onToggle }) {
  const [openDomain, setOpenDomain] = useState(null)
  if (!taxonomy) return (
    <div style={{ fontSize:11, color:'#aaa' }}>Loading indicator list…</div>
  )
  return (
    <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
      {taxonomy.domains.map(dom => {
        const inds = taxonomy.indicators.filter(i => i.domainId === dom.id)
        const selCount = inds.filter(i => selected.includes(i.id)).length
        const open = openDomain === dom.id
        return (
          <div key={dom.id} style={{ border:'1.5px solid #e0e7f0', borderRadius:9, overflow:'hidden' }}>
            <button onClick={() => setOpenDomain(open ? null : dom.id)}
              style={{ width:'100%', display:'flex', alignItems:'center', gap:8, padding:'8px 12px',
                       background: selCount > 0 ? '#EBF4FB' : 'white', border:'none', cursor:'pointer', textAlign:'left' }}>
              <span style={{ fontSize:15 }}>{dom.icon}</span>
              <span style={{ flex:1, fontSize:12, fontWeight:700, color:'#003366' }}>{dom.label}</span>
              {selCount > 0 && (
                <span style={{ fontSize:10, fontWeight:800, color:'#003366', background:'white',
                               border:'1px solid #BDD7EE', borderRadius:10, padding:'1px 7px' }}>{selCount}</span>
              )}
              <span style={{ fontSize:11, color:'#888' }}>{open ? '▲' : '▼'}</span>
            </button>
            {open && (
              <div style={{ display:'flex', flexDirection:'column', gap:5, padding:'9px 11px', background:'#FAFCFF' }}>
                {inds.map(ind => {
                  const on = selected.includes(ind.id)
                  return (
                    <button key={ind.id} onClick={() => onToggle(ind.id)}
                      style={{ display:'flex', alignItems:'center', gap:9, width:'100%', textAlign:'left',
                               padding:'7px 10px', borderRadius:8, cursor:'pointer',
                               border:`1.5px solid ${on ? '#003366' : '#e0e7f0'}`,
                               background: on ? '#EBF4FB' : 'white' }}>
                      <span style={{ flexShrink:0, fontSize:9, fontWeight:800, letterSpacing:.4,
                                     color: on ? 'white' : '#888', background: on ? '#003366' : '#f0f4f8',
                                     padding:'2px 6px', borderRadius:5 }}>{ind.id.toUpperCase()}</span>
                      <span style={{ flex:1, fontSize:12, fontWeight:600, color: on ? '#003366' : '#444', lineHeight:1.35 }}>{ind.label}</span>
                      {on && <span style={{ flexShrink:0, fontSize:13, color:'#003366', fontWeight:800 }}>✓</span>}
                    </button>
                  )
                })}
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}

// ── EditModal ─────────────────────────────────────────────────────────────────
function EditModal({ item, taxonomy, onSave, onClose, onDelete }) {
  const [form, setForm] = useState({
    labels:     { ...item.labels },
    category:   item.category,
    tags:       [...(item.tags    || [])],
    modules:    [...(item.modules || [])],
    indicators: [...(item.indicators || [])],
  })
  const [tagInput, setTagInput] = useState('')
  const [saving, setSaving] = useState(false)
  const addTag = () => {
    const t = tagInput.trim().toLowerCase()
    if (t && !form.tags.includes(t)) setForm(f => ({ ...f, tags: [...f.tags, t] }))
    setTagInput('')
  }
  const toggleIndicator = (id) => {
    setForm(f => ({
      ...f,
      indicators: f.indicators.includes(id)
        ? f.indicators.filter(x => x !== id)
        : [...f.indicators, id],
    }))
  }
  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.5)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:200, padding:16 }} onClick={onClose}>
      <div style={{ background:'white', borderRadius:18, padding:28, width:520, maxWidth:'95vw', maxHeight:'90vh', overflowY:'auto', boxShadow:'0 24px 60px rgba(0,0,0,.25)' }} onClick={e => e.stopPropagation()}>
        <div style={{ display:'flex', alignItems:'flex-start', gap:16, marginBottom:22 }}>
          <img src={item.url} style={{ width:72, height:72, borderRadius:10, objectFit:'contain', border:'1.5px solid #e0e7f0', background:'#f8f9fc' }} alt=""/>
          <div style={{ flex:1 }}>
            <div style={{ fontWeight:800, fontSize:17, color:'#1a1a2e', marginBottom:4 }}>{item.labels?.en || 'Untitled'}</div>
            <div style={{ fontSize:11, color:'#aaa' }}>{item.filename} · {item.size}</div>
          </div>
          <button onClick={onClose} style={{ background:'#f0f4ff', border:'none', borderRadius:8, padding:'6px 10px', cursor:'pointer', fontSize:16, color:'#666' }}>×</button>
        </div>

        <div style={{ marginBottom:16 }}>
          <div style={{ fontSize:11, fontWeight:700, color:'#888', marginBottom:10, letterSpacing:.5 }}>LABELS (Multilingual)</div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8 }}>
            {LANGUAGES.map(l => (
              <div key={l.code}>
                <div style={{ fontSize:10, color:'#aaa', marginBottom:3 }}>{l.name}</div>
                <input value={form.labels[l.code] || ''} onChange={e => setForm(f => ({ ...f, labels: { ...f.labels, [l.code]: e.target.value } }))} placeholder={`Label in ${l.name}`} style={{ width:'100%', border:'1.5px solid #dce3f5', borderRadius:7, padding:'7px 10px', fontSize:13, outline:'none', fontFamily:'inherit' }}/>
              </div>
            ))}
          </div>
        </div>

        <div style={{ marginBottom:16 }}>
          <div style={{ fontSize:11, fontWeight:700, color:'#888', marginBottom:8, letterSpacing:.5 }}>CATEGORY</div>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:6 }}>
            {CATEGORIES.map(c => (
              <button key={c.id} onClick={() => setForm(f => ({ ...f, category: c.id }))} style={{ padding:'7px 6px', border: form.category === c.id ? '2px solid #003366' : '1.5px solid #e0e7f0', borderRadius:8, background: form.category === c.id ? '#e8f0fe' : 'white', cursor:'pointer', fontSize:10, fontWeight:700, color: form.category === c.id ? '#003366' : '#666', textAlign:'center' }}>
                <div style={{ fontSize:14 }}>{c.icon}</div>{c.label}
              </button>
            ))}
          </div>
        </div>

        <div style={{ marginBottom:16 }}>
          <div style={{ fontSize:11, fontWeight:700, color:'#888', marginBottom:8, letterSpacing:.5 }}>TAGS</div>
          <div style={{ display:'flex', flexWrap:'wrap', gap:6, marginBottom:8 }}>
            {form.tags.map(t => (
              <span key={t} style={{ background:'#e8f0fe', color:'#003366', fontSize:11, fontWeight:700, padding:'3px 10px', borderRadius:12, display:'flex', alignItems:'center', gap:5 }}>
                {t}<span onClick={() => setForm(f => ({ ...f, tags: f.tags.filter(x => x !== t) }))} style={{ cursor:'pointer', fontSize:12, color:'#aaa' }}>×</span>
              </span>
            ))}
          </div>
          <div style={{ display:'flex', gap:8 }}>
            <input value={tagInput} onChange={e => setTagInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && addTag()} placeholder="Add tag…" style={{ flex:1, border:'1.5px solid #dce3f5', borderRadius:7, padding:'7px 10px', fontSize:12, outline:'none' }}/>
            <button onClick={addTag} style={{ padding:'7px 14px', background:'#003366', color:'white', border:'none', borderRadius:7, fontWeight:700, fontSize:12, cursor:'pointer' }}>Add</button>
          </div>
        </div>

        <div style={{ marginBottom:16 }}>
          <div style={{ fontSize:11, fontWeight:700, color:'#888', marginBottom:8, letterSpacing:.5 }}>
            DEVELOPMENT INDICATORS (NCF)
            <span style={{ fontWeight:400, textTransform:'none', letterSpacing:0, color:'#bbb' }}> — what skill(s) does this image help practice?</span>
          </div>
          <IndicatorTagPicker taxonomy={taxonomy} selected={form.indicators} onToggle={toggleIndicator}/>
          {form.indicators.length > 0 && (
            <div style={{ display:'flex', flexWrap:'wrap', gap:5, marginTop:8 }}>
              {form.indicators.map(id => {
                const ind = taxonomy?.indicators.find(i => i.id === id)
                return (
                  <span key={id} style={{ background:'#EBF4FB', color:'#003366', fontSize:10, fontWeight:700, padding:'2px 8px', borderRadius:10 }}>
                    {id}{ind ? ` · ${ind.label}` : ''}
                  </span>
                )
              })}
            </div>
          )}
        </div>

        <div style={{ marginBottom:20 }}>
          <div style={{ fontSize:11, fontWeight:700, color:'#888', marginBottom:8, letterSpacing:.5 }}>USED IN MODULES</div>
          <div style={{ display:'flex', flexWrap:'wrap', gap:6 }}>
            {MODULES.map(m => {
              const on = form.modules.includes(m.id)
              return (
                <button key={m.id} onClick={() => setForm(f => ({ ...f, modules: on ? f.modules.filter(x => x !== m.id) : [...f.modules, m.id] }))} style={{ padding:'5px 12px', border:`1.5px solid ${on ? '#003366' : '#e0e7f0'}`, borderRadius:8, background: on ? '#e8f0fe' : 'white', color: on ? '#003366' : '#888', fontSize:11, fontWeight:700, cursor:'pointer' }}>
                  {m.icon} {m.label}
                </button>
              )
            })}
          </div>
        </div>

        <div style={{ display:'flex', gap:10 }}>
          <button onClick={() => onDelete(item.id)} style={{ padding:'10px 16px', background:'#fff0f0', color:'#c0392b', border:'1.5px solid #f5c6c6', borderRadius:8, fontWeight:700, fontSize:13, cursor:'pointer' }}>🗑 Delete</button>
          <div style={{ flex:1 }}/>
          <button onClick={onClose} style={{ padding:'10px 18px', background:'white', color:'#666', border:'1.5px solid #dce3f5', borderRadius:8, fontWeight:700, fontSize:13, cursor:'pointer' }}>Cancel</button>
          <button
            onClick={async () => { setSaving(true); await onSave({ ...item, ...form }); setSaving(false) }}
            disabled={saving}
            style={{ padding:'10px 22px', background:saving?'#8A9DB5':'#003366', color:'white', border:'none', borderRadius:8, fontWeight:700, fontSize:13, cursor:saving?'wait':'pointer' }}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ── ImageCard ─────────────────────────────────────────────────────────────────
function ImageCard({ item, selected, onSelect, onEdit, onView, viewMode, activeLanguage, isOwn, taxonomy }) {
  const cat   = CATEGORIES.find(c => c.id === item.category)
  const label = item.labels?.[activeLanguage] || item.labels?.en || item.title || item.filename?.replace(/\.[^.]+$/, '') || 'Untitled'
  const indicatorCount = item.indicators?.length || 0
  const indicatorLabels = item.indicators?.map(id => taxonomy?.indicators.find(i => i.id === id)?.label || id).join(', ')
  const { pending, missing } = metaStatus(item)
  const pendingTitle = `Needs: ${missing.join(', ')}`

  if (viewMode === 'list') {
    return (
      <div style={{ display:'flex', alignItems:'center', gap:12, padding:'10px 14px',
        background: selected ? '#e8f0fe' : 'white',
        border: selected ? '1.5px solid #003366' : '1.5px solid #e0e7f0',
        borderRadius:10, cursor:'pointer' }}>
        <input type="checkbox" checked={selected} onChange={e => { e.stopPropagation(); onSelect() }}
          style={{ width:16, height:16, accentColor:'#003366', flexShrink:0 }}/>
        <img src={item.thumbnail} onClick={e => { e.stopPropagation(); onView() }}
          style={{ width:44, height:44, borderRadius:6, objectFit:'contain', background:'#f8f9fc', flexShrink:0, cursor:'zoom-in' }} alt=""/>
        <div style={{ flex:1, minWidth:0 }}>
          <div style={{ fontWeight:700, fontSize:13, color:'#1a1a2e', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{label}</div>
          <div style={{ fontSize:10, color:'#aaa', marginTop:2, display:'flex', gap:8, alignItems:'center' }}>
            <span>{item.filename} · {item.size}</span>
            {isOwn && <span style={{ background:'#e8f0fe', color:'#003366', padding:'1px 6px', borderRadius:6, fontWeight:700 }}>Mine</span>}
            {item.uploaderName && !isOwn && <span style={{ color:'#bbb' }}>by {item.uploaderName}</span>}
          </div>
        </div>
        {indicatorCount > 0 && (
          <span style={{ fontSize:10, background:'#EBF4FB', color:'#003366', padding:'2px 8px', borderRadius:8, fontWeight:700 }} title={indicatorLabels}>
            🎯 {indicatorCount}
          </span>
        )}
        {pending && (
          <span style={{ fontSize:10, background:'#FFF4E5', color:'#A06800', padding:'2px 8px', borderRadius:8, fontWeight:700 }} title={pendingTitle}>
            ⏳ Pending
          </span>
        )}
        <span style={{ fontSize:11, background:'#f0f4ff', color:'#003366', padding:'2px 8px', borderRadius:8, fontWeight:700 }}>{cat?.icon} {cat?.label}</span>
        {isOwn && <button onClick={e => { e.stopPropagation(); onEdit() }}
          style={{ background:'#f0f4ff', border:'none', borderRadius:6, padding:'5px 10px', fontSize:12, cursor:'pointer', color:'#003366', fontWeight:700 }}>✎ Edit</button>}
        <button onClick={e => { e.stopPropagation(); onView() }}
          style={{ background:'#f8f9fc', border:'none', borderRadius:6, padding:'5px 10px', fontSize:12, cursor:'pointer', color:'#555' }}>🔍</button>
      </div>
    )
  }

  return (
    <div style={{ border: selected ? '2.5px solid #003366' : '2px solid #e0e7f0',
      borderRadius:12, overflow:'hidden', background:'white', position:'relative',
      boxShadow: selected ? '0 0 0 3px #003366' : 'none' }}>
      {/* Checkbox */}
      <div style={{ position:'absolute', top:7, left:7, zIndex:2 }} onClick={e => e.stopPropagation()}>
        <input type="checkbox" checked={selected} onChange={onSelect}
          style={{ width:16, height:16, accentColor:'#003366', cursor:'pointer' }}/>
      </div>
      {/* Ownership badge */}
      {isOwn && (
        <div style={{ position:'absolute', top:7, left:28, zIndex:2,
          background:'#003366', color:'white', fontSize:8, fontWeight:800,
          padding:'2px 6px', borderRadius:6, letterSpacing:.3 }}>MINE</div>
      )}
      {/* Indicator-tagged badge */}
      {indicatorCount > 0 && (
        <div style={{ position:'absolute', bottom:7, left:7, zIndex:2,
          background:'rgba(0,51,102,.85)', color:'white', fontSize:9, fontWeight:800,
          padding:'2px 7px', borderRadius:6 }} title={indicatorLabels}>
          🎯 {indicatorCount}
        </div>
      )}
      {/* Pending badge — missing tags and/or indicators */}
      {pending && (
        <div style={{ position:'absolute', bottom:7, right:7, zIndex:2,
          background:'rgba(184,134,11,.92)', color:'white', fontSize:9, fontWeight:800,
          padding:'2px 7px', borderRadius:6 }} title={pendingTitle}>
          ⏳ Pending
        </div>
      )}
      {/* Edit button — only for own images */}
      {isOwn && (
        <button onClick={e => { e.stopPropagation(); onEdit() }}
          style={{ position:'absolute', top:6, right:6, background:'rgba(255,255,255,.92)',
            border:'none', borderRadius:6, padding:'3px 8px', fontSize:11,
            cursor:'pointer', color:'#003366', fontWeight:700, zIndex:2 }}>✎</button>
      )}
      {/* Image — click to open lightbox */}
      <div onClick={onView}
        style={{ height:110, display:'flex', alignItems:'center', justifyContent:'center',
          background:'#f8f9fc', overflow:'hidden', cursor:'zoom-in' }}>
        <img src={item.thumbnail} style={{ maxWidth:'100%', maxHeight:'100%', objectFit:'contain' }} alt={label}/>
      </div>
      <div style={{ padding:'8px 10px' }}>
        <div style={{ fontSize:12, fontWeight:700, color:'#1a1a2e', marginBottom:3,
          overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{label}</div>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between' }}>
          <span style={{ fontSize:10, color:'#aaa' }}>{cat?.icon} {cat?.label}</span>
          {item.uploaderName && !isOwn && <span style={{ fontSize:9, color:'#ccc' }}>{item.uploaderName}</span>}
        </div>
        {item.tags?.length > 0 && (
          <div style={{ display:'flex', flexWrap:'wrap', gap:3, marginTop:5 }}>
            {item.tags.slice(0, 3).map(t =>
              <span key={t} style={{ fontSize:9, background:'#f0f4ff', color:'#003366', padding:'1px 6px', borderRadius:8 }}>{t}</span>)}
          </div>
        )}
      </div>
    </div>
  )
}

// ── Lightbox ─────────────────────────────────────────────────────────────────
function Lightbox({ item, onClose, onEdit, isOwn, activeLanguage, taxonomy }) {
  const label = item.labels?.[activeLanguage] || item.labels?.en || item.title || item.filename || 'Image'
  const cat   = CATEGORIES.find(c => c.id === item.category)
  useEffect(() => {
    const handler = e => e.key === 'Escape' && onClose()
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [onClose])
  return (
    <div onClick={onClose}
      style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.85)', zIndex:300,
        display:'flex', alignItems:'center', justifyContent:'center', padding:20 }}>
      <div onClick={e => e.stopPropagation()}
        style={{ background:'white', borderRadius:16, overflow:'hidden', maxWidth:740,
          width:'100%', maxHeight:'90vh', display:'flex', flexDirection:'column',
          boxShadow:'0 24px 80px rgba(0,0,0,.5)' }}>
        {/* Header */}
        <div style={{ display:'flex', alignItems:'center', padding:'14px 18px',
          borderBottom:'1px solid #e0e7f0', gap:12 }}>
          <div style={{ flex:1, minWidth:0 }}>
            <div style={{ fontWeight:800, fontSize:15, color:'#1a1a2e',
              overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{label}</div>
            <div style={{ fontSize:11, color:'#aaa', marginTop:2, display:'flex', gap:10 }}>
              {cat && <span>{cat.icon} {cat.label}</span>}
              {item.size && <span>{item.size}</span>}
              {item.uploaderName && <span>by {item.uploaderName}</span>}
              {isOwn && <span style={{ background:'#003366', color:'white', padding:'1px 7px', borderRadius:6, fontWeight:700, fontSize:9 }}>YOUR IMAGE</span>}
            </div>
          </div>
          {isOwn && (
            <button onClick={onEdit}
              style={{ padding:'7px 14px', background:'#003366', color:'white', border:'none',
                borderRadius:8, fontWeight:700, fontSize:12, cursor:'pointer' }}>✎ Edit</button>
          )}
          <button onClick={onClose}
            style={{ padding:'7px 11px', background:'#f0f4f8', border:'none',
              borderRadius:8, fontWeight:700, fontSize:14, cursor:'pointer', color:'#555' }}>✕</button>
        </div>
        {/* Full image */}
        <div style={{ flex:1, overflow:'auto', display:'flex', alignItems:'center',
          justifyContent:'center', background:'#f8f9fc', padding:20, minHeight:300 }}>
          <img src={item.url} alt={label}
            style={{ maxWidth:'100%', maxHeight:'60vh', objectFit:'contain',
              borderRadius:8, boxShadow:'0 4px 20px rgba(0,0,0,.1)' }}/>
        </div>
        {/* Footer meta */}
        <div style={{ padding:'12px 18px', borderTop:'1px solid #e0e7f0',
          display:'flex', gap:8, flexWrap:'wrap', alignItems:'center' }}>
          {LANGUAGES.map(l => {
            const lbl = item.labels?.[l.code]
            if (!lbl) return null
            return (
              <span key={l.code} style={{ fontSize:11, background:'#f0f4ff', color:'#003366',
                padding:'3px 10px', borderRadius:8, fontWeight:600 }}>
                {l.name}: {lbl}
              </span>
            )
          })}
          {item.indicators?.length > 0 && item.indicators.map(id => {
            const ind = taxonomy?.indicators.find(i => i.id === id)
            return (
              <span key={id} style={{ fontSize:10, background:'#EBF4FB', color:'#003366',
                padding:'2px 9px', borderRadius:8, fontWeight:700 }} title={ind ? id.toUpperCase() : undefined}>
                🎯 {ind ? ind.label : id}
              </span>
            )
          })}
          {item.tags?.length > 0 && item.tags.map(t =>
            <span key={t} style={{ fontSize:10, background:'#f5f5f5', color:'#888',
              padding:'2px 8px', borderRadius:8 }}>#{t}</span>
          )}
          {item.filename && (
            <a href={item.url} download={item.filename} target="_blank" rel="noreferrer"
              style={{ marginLeft:'auto', fontSize:11, fontWeight:700, color:'#003366',
                textDecoration:'none', background:'#e8f0fe', padding:'5px 12px', borderRadius:8 }}>
              ⬇ Download
            </a>
          )}
        </div>
      </div>
    </div>
  )
}

// ── Main ──────────────────────────────────────────────────────────────────────
export default function CollectionManager() {
  // StoreContext: collection is server-only; no store writes needed
  const { getAuthHeader, user } = useAuth()
  const { lang } = useLang()

  // Start empty — populated entirely from server, no hardcoded seeds
  const [items,      setItems]      = useState([])
  const [loading,    setLoading]    = useState(true)
  const [loadError,  setLoadError]  = useState(null)
  const [toast,      setToast]      = useState(null)
  const [lightbox,   setLightbox]   = useState(null) // item to show full-size

  // Shared NCF development taxonomy (40 indicators / 5 domains) — single
  // source of truth fetched from the backend, used for tagging + filtering
  // + coverage stats. Same taxonomy will back Flashcard/Worksheet/Activity/
  // StoryBook tagging as those creators get updated.
  const [taxonomy, setTaxonomy] = useState(null)

  const showToast = (msg, type = 'ok') => {
    setToast({ msg, type })
    setTimeout(() => setToast(null), 3000)
  }

  useEffect(() => {
    async function fetchServerImages() {
      setLoading(true)
      setLoadError(null)
      try {
        const res  = await fetch(`${API}/collection?limit=200`, { headers: getAuthHeader() })
        const data = await res.json()
        if (!data.success) throw new Error(data.message || 'Failed to load images')
        setItems(data.images.map(serverDocToItem))
      } catch (err) {
        setLoadError(err.message || 'Could not load images from server')
      } finally {
        setLoading(false)
      }
    }
    fetchServerImages()
  }, [])

  useEffect(() => {
    async function fetchTaxonomy() {
      try {
        const res  = await fetch(`${API}/collection/taxonomy`, { headers: getAuthHeader() })
        const data = await res.json()
        if (data.success) setTaxonomy(data.taxonomy)
      } catch {
        // non-fatal — tagging UI just shows "Loading indicator list…"
      }
    }
    fetchTaxonomy()
  }, [])

  const [selected,     setSelected]     = useState(new Set())
  const [editItem,     setEditItem]     = useState(null)
  const [search,       setSearch]       = useState('')
  const [filterCat,    setFilterCat]    = useState('all')
  const [filterModule, setFilterModule] = useState('all')
  const [filterDomain, setFilterDomain] = useState('all')
  const [sortBy,       setSortBy]       = useState('date-desc')
  const [viewMode,     setViewMode]     = useState('grid')
  const [activeTab,    setActiveTab]    = useState('library')
  const [activeLang,   setActiveLang]   = useState(lang || 'en')
  const [bulkAction,   setBulkAction]   = useState('')

  let filtered = items
  if (search) {
    const q = search.toLowerCase()
    filtered = filtered.filter(it =>
      Object.values(it.labels || {}).some(v => v.toLowerCase().includes(q)) ||
      it.tags?.some(t => t.includes(q)) ||
      it.filename?.toLowerCase().includes(q)
    )
  }
  if (filterCat    !== 'all') filtered = filtered.filter(it => it.category === filterCat)
  if (filterModule !== 'all') filtered = filtered.filter(it => it.modules?.includes(filterModule))
  if (filterDomain !== 'all') {
    filtered = filterDomain === 'untagged'
      ? filtered.filter(it => !it.indicators || it.indicators.length === 0)
      : filtered.filter(it => it.domains?.includes(filterDomain))
  }

  const sorted = [...filtered].sort((a, b) => {
    if (sortBy === 'name-asc')  return (a.labels?.en || '').localeCompare(b.labels?.en || '')
    if (sortBy === 'name-desc') return (b.labels?.en || '').localeCompare(a.labels?.en || '')
    if (sortBy === 'date-asc')  return new Date(a.addedAt) - new Date(b.addedAt)
    if (sortBy === 'date-desc') return new Date(b.addedAt) - new Date(a.addedAt)
    if (sortBy === 'category')  return (a.category || '').localeCompare(b.category || '')
    return 0
  })

  const addItem = (item) => {
    setItems(p => [item, ...p])
    // server-only — no StoreContext writes
  }
  const saveItem = async (upd) => {
    const rollback = items.find(it => it.id === upd.id)
    setItems(p => p.map(it => it.id === upd.id ? upd : it))
    if (!upd.serverId) { showToast('Saved locally', 'ok'); return }
    try {
      const res  = await fetch(`${API}/collection/${upd.serverId}`, {
        method: 'PUT',
        headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title:       upd.labels?.en || '',
          labels:      upd.labels     || {},
          category:    upd.category   || 'custom',
          tags:        upd.tags       || [],
          description: upd.description || '',
          indicators:  upd.indicators || [],
        }),
      })
      const data = await res.json()
      if (!res.ok || !data.success) throw new Error(data.message || `Error ${res.status}`)
      setItems(p => p.map(it => it.id === upd.id ? { ...upd, ...serverDocToItem(data.image) } : it))
      showToast('✅ Saved', 'ok')
    } catch (err) {
      if (rollback) setItems(p => p.map(it => it.id === upd.id ? rollback : it))
      showToast('❌ ' + err.message, 'err')
    }
  }

  const deleteItem = async (id) => {
    const item = items.find(it => it.id === id)
    if (item?.serverId) {
      try {
        const res = await fetch(`${API}/collection/${item.serverId}`, {
          method: 'DELETE', headers: getAuthHeader(),
        })
        if (!res.ok && res.status !== 404) {
          const d = await res.json().catch(() => ({}))
          throw new Error(d.message || `Error ${res.status}`)
        }
      } catch (err) {
        showToast('❌ Delete failed: ' + err.message, 'err')
        return
      }
    }
    setItems(p => p.filter(it => it.id !== id))
    setEditItem(null)
    setSelected(s => { s.delete(id); return new Set(s) })
    showToast('🗑 Deleted', 'ok')
  }

  const toggleSelect = (id) => setSelected(p => { const s = new Set(p); s.has(id) ? s.delete(id) : s.add(id); return s })
  const selectAll    = ()   => setSelected(new Set(sorted.map(it => it.id)))
  const clearSelect  = ()   => setSelected(new Set())

  const applyBulk = async () => {
    if (!bulkAction || selected.size === 0) return
    const authHeader  = getAuthHeader()
    const jsonHeaders = { ...authHeader, 'Content-Type': 'application/json' }

    if (bulkAction === 'delete') {
      const toDelete    = items.filter(it => selected.has(it.id))
      const serverItems = toDelete.filter(it => it.serverId)

      if (serverItems.length > 0) {
        const res  = await fetch(`${API}/collection/bulk/delete`, {
          method: 'DELETE', headers: jsonHeaders,
          body: JSON.stringify({ ids: serverItems.map(it => it.serverId) }),
        }).catch(() => null)
        const data = await res?.json().catch(() => ({})) || {}
        if (!res?.ok) {
          showToast(`❌ ${data.message || 'Delete failed'}`, 'err')
          clearSelect(); setBulkAction(''); return
        }
        if (data.skipped > 0) showToast(`⚠ ${data.message}`, 'err')
        else showToast(`🗑 ${data.message || `Deleted ${data.deleted} image(s)`}`, 'ok')
      }

      // Re-fetch to sync with server
      const res2  = await fetch(`${API}/collection?limit=200`, { headers: authHeader }).catch(() => null)
      const data2 = await res2?.json().catch(() => null)
      if (data2?.success) setItems(data2.images.map(serverDocToItem))
      else setItems(p => p.filter(it => !selected.has(it.id)))
      clearSelect(); setBulkAction(''); return
    }

    if (bulkAction.startsWith('cat-')) {
      const cat      = bulkAction.replace('cat-', '')
      const toUpdate = items.filter(it => selected.has(it.id) && it.serverId)
      let success = 0

      for (const it of toUpdate) {
        const res = await fetch(`${API}/collection/${it.serverId}`, {
          method: 'PUT', headers: jsonHeaders,
          body: JSON.stringify({
            title:    itemName(it),
            labels:   it.labels    || {},
            category: cat,
            tags:     it.tags      || [],
          }),
        }).catch(() => null)
        const d = await res?.json().catch(() => ({})) || {}
        if (res?.ok && d.success) success++
        else showToast(`❌ Update failed for "${itemName(it)}"`, 'err')
      }

      if (success > 0) {
        setItems(p => p.map(it => selected.has(it.id) ? { ...it, category: cat } : it))
        showToast(`✅ Category updated for ${success} image${success !== 1 ? 's' : ''}`, 'ok')
      }
      clearSelect(); setBulkAction(''); return
    }

    if (bulkAction.startsWith('mod-')) {
      const mod = bulkAction.replace('mod-', '')
      setItems(p => p.map(it =>
        selected.has(it.id) ? { ...it, modules: [...new Set([...(it.modules || []), mod])] } : it
      ))
      showToast('✅ Module assigned', 'ok')
      clearSelect(); setBulkAction(''); return
    }

    if (bulkAction.startsWith('ind-')) {
      const indicator = bulkAction.replace('ind-', '')
      const ids       = items.filter(it => selected.has(it.id) && it.serverId).map(it => it.serverId)
      if (!ids.length) { clearSelect(); setBulkAction(''); return }

      const res  = await fetch(`${API}/collection/bulk/tag`, {
        method: 'PUT', headers: jsonHeaders,
        body: JSON.stringify({ ids, indicators: [indicator], mode: 'add' }),
      }).catch(() => null)
      const data = await res?.json().catch(() => ({})) || {}

      if (res?.ok && data.success) {
        setItems(p => p.map(it => selected.has(it.id)
          ? { ...it, indicators: [...new Set([...(it.indicators || []), indicator])] }
          : it))
        showToast(`✅ ${data.message || `Tagged ${data.updated} image(s)`}`, 'ok')
      } else {
        showToast(`❌ ${data.message || 'Tagging failed'}`, 'err')
      }
      clearSelect(); setBulkAction(''); return
    }
  }

  const catCounts = CATEGORIES.map(c => ({ ...c, count: items.filter(it => it.category === c.id).length })).filter(c => c.count > 0).sort((a,b) => b.count - a.count)

  // Coverage: how many images are tagged with each of the 40 NCF indicators
  // (computed client-side from already-loaded items — cheap, and avoids an
  // extra round-trip on every Library render; /collection/coverage is also
  // available server-side for a school-wide view independent of pagination).
  const indicatorCoverage = taxonomy
    ? taxonomy.indicators.map(ind => ({
        ...ind,
        imageCount: items.filter(it => it.indicators?.includes(ind.id)).length,
      }))
    : []
  const untaggedCount = items.filter(it => !it.indicators || it.indicators.length === 0).length

  return (
    <div style={{ fontFamily:"'DM Sans',system-ui,sans-serif", background:'#F5F7FA', minHeight:'100vh', color:'#1a1a2e' }}>

      {/* TABS */}
      <div style={{ background:'white', borderBottom:'1px solid #e0e7f0', padding:'0 24px', display:'flex', alignItems:'center' }}>
        {[['library','🗂️ Library'],['upload','📤 Upload'],['stats','📊 Stats']].map(([t,l]) => (
          <button key={t} onClick={() => setActiveTab(t)} style={{ background:'none', border:'none', padding:'13px 20px', fontSize:13, fontWeight:700, color: activeTab===t ? '#003366' : '#888', borderBottom:`3px solid ${activeTab===t ? '#003366' : 'transparent'}`, cursor:'pointer' }}>{l}</button>
        ))}
        <div style={{ flex:1 }}/>
        <div style={{ fontSize:12, color:'#888' }}>
          {loading
            ? <span style={{ color:'#aaa' }}>Loading…</span>
            : <><span style={{ fontWeight:700, color:'#003366' }}>{items.length}</span> images ·{' '}
               <span style={{ fontWeight:700, color:'#003366' }}>{filtered.length}</span> shown
               {selected.size > 0 && <> · <span style={{ fontWeight:700, color:'#E8634A' }}>{selected.size} selected</span></>}</>
          }
        </div>
      </div>

      {/* LIBRARY */}
      {activeTab === 'library' && (
        <div style={{ padding:24 }}>

          {loading && (
            <div style={{ textAlign:'center', padding:'80px 0', color:'#aaa' }}>
              <div style={{ fontSize:36, marginBottom:12 }}>⏳</div>
              <div style={{ fontWeight:700, fontSize:15, color:'#003366' }}>Loading your images…</div>
              <div style={{ fontSize:12, marginTop:4 }}>Fetching from server</div>
            </div>
          )}

          {!loading && loadError && (
            <div style={{ textAlign:'center', padding:'60px 0' }}>
              <div style={{ fontSize:36, marginBottom:12 }}>⚠️</div>
              <div style={{ fontWeight:700, fontSize:15, color:'#c0392b', marginBottom:6 }}>Could not load images</div>
              <div style={{ fontSize:12, color:'#888', marginBottom:16 }}>{loadError}</div>
              <button onClick={() => window.location.reload()} style={{ padding:'8px 20px', background:'#003366', color:'white', border:'none', borderRadius:8, fontWeight:700, fontSize:12, cursor:'pointer' }}>
                Try Again
              </button>
            </div>
          )}

          {!loading && !loadError && items.length === 0 && (
            <div style={{ textAlign:'center', padding:'80px 24px' }}>
              <div style={{ fontSize:52, marginBottom:16 }}>🖼️</div>
              <div style={{ fontWeight:800, fontSize:18, color:'#003366', marginBottom:6 }}>No images yet</div>
              <div style={{ fontSize:13, color:'#888', marginBottom:24, lineHeight:1.7 }}>
                Your collection is empty.<br/>Upload images to start building your library.
              </div>
              <button onClick={() => setActiveTab('upload')} style={{ padding:'10px 28px', background:'#003366', color:'white', border:'none', borderRadius:10, fontWeight:800, fontSize:13, cursor:'pointer' }}>
                📤 Upload Images
              </button>
            </div>
          )}

          {!loading && !loadError && items.length > 0 && (
            <>
              <div style={{ display:'flex', gap:10, marginBottom:16, flexWrap:'wrap', alignItems:'center' }}>
                <div style={{ position:'relative', flex:1, minWidth:200 }}>
                  <span style={{ position:'absolute', left:10, top:'50%', transform:'translateY(-50%)', fontSize:15, color:'#aaa' }}>🔍</span>
                  <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by name, tag…" style={{ width:'100%', border:'1.5px solid #dce3f5', borderRadius:10, padding:'9px 12px 9px 34px', fontSize:13, outline:'none', background:'white' }}/>
                </div>
                <select value={filterCat} onChange={e => setFilterCat(e.target.value)} style={{ border:'1.5px solid #dce3f5', borderRadius:10, padding:'9px 12px', fontSize:12, outline:'none', background:'white', fontWeight:600, color:'#555' }}>
                  <option value="all">All Categories</option>
                  {CATEGORIES.map(c => <option key={c.id} value={c.id}>{c.icon} {c.label}</option>)}
                </select>
                <select value={filterModule} onChange={e => setFilterModule(e.target.value)} style={{ border:'1.5px solid #dce3f5', borderRadius:10, padding:'9px 12px', fontSize:12, outline:'none', background:'white', fontWeight:600, color:'#555' }}>
                  <option value="all">All Modules</option>
                  {MODULES.map(m => <option key={m.id} value={m.id}>{m.icon} {m.label}</option>)}
                </select>
                <select value={filterDomain} onChange={e => setFilterDomain(e.target.value)} style={{ border:'1.5px solid #dce3f5', borderRadius:10, padding:'9px 12px', fontSize:12, outline:'none', background:'white', fontWeight:600, color:'#555' }}>
                  <option value="all">All Domains</option>
                  {taxonomy?.domains.map(d => <option key={d.id} value={d.id}>{d.icon} {d.label}</option>)}
                  <option value="untagged">🏷️ Untagged only</option>
                </select>
                <select value={sortBy} onChange={e => setSortBy(e.target.value)} style={{ border:'1.5px solid #dce3f5', borderRadius:10, padding:'9px 12px', fontSize:12, outline:'none', background:'white', fontWeight:600, color:'#555' }}>
                  {SORT_OPTIONS.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
                </select>
                <select value={activeLang} onChange={e => setActiveLang(e.target.value)} style={{ border:'1.5px solid #dce3f5', borderRadius:10, padding:'9px 12px', fontSize:12, outline:'none', background:'white', fontWeight:600, color:'#003366' }}>
                  {LANGUAGES.map(l => <option key={l.code} value={l.code}>{l.name}</option>)}
                </select>
                <div style={{ display:'flex', background:'#f0f4ff', borderRadius:8, overflow:'hidden' }}>
                  {[['grid','▦'],['list','☰']].map(([v,ic]) => (
                    <button key={v} onClick={() => setViewMode(v)} style={{ padding:'8px 14px', border:'none', background: viewMode===v ? '#003366' : 'transparent', color: viewMode===v ? 'white' : '#666', cursor:'pointer', fontWeight:700, fontSize:14 }}>{ic}</button>
                  ))}
                </div>
              </div>

              <div style={{ display:'flex', gap:8, marginBottom:16, alignItems:'center', flexWrap:'wrap' }}>
                <button onClick={selectAll}   style={{ padding:'6px 14px', background:'#e8f0fe', color:'#003366', border:'none', borderRadius:6, fontSize:12, cursor:'pointer', fontWeight:700 }}>Select All ({sorted.length})</button>
                <button onClick={clearSelect} style={{ padding:'6px 14px', background:'white', color:'#666', border:'1px solid #dce3f5', borderRadius:6, fontSize:12, cursor:'pointer', fontWeight:600 }}>Clear</button>
                {selected.size > 0 && (
                  <>
                    <span style={{ fontSize:12, color:'#E8634A', fontWeight:700 }}>{selected.size} selected</span>
                    <select value={bulkAction} onChange={e => setBulkAction(e.target.value)} style={{ border:'1.5px solid #dce3f5', borderRadius:8, padding:'6px 10px', fontSize:12, outline:'none' }}>
                      <option value="">Bulk action…</option>
                      <optgroup label="Set Category">{CATEGORIES.map(c => <option key={c.id} value={`cat-${c.id}`}>{c.icon} {c.label}</option>)}</optgroup>
                      <optgroup label="Add to Module">{MODULES.map(m => <option key={m.id} value={`mod-${m.id}`}>{m.icon} {m.label}</option>)}</optgroup>
                      {taxonomy && (
                        <optgroup label="Tag with Indicator (NCF)">
                          {taxonomy.domains.map(dom => (
                            taxonomy.indicators.filter(i => i.domainId === dom.id).map(ind => (
                              <option key={ind.id} value={`ind-${ind.id}`}>{dom.icon} {ind.id} — {ind.label}</option>
                            ))
                          ))}
                        </optgroup>
                      )}
                      <option value="delete">🗑 Delete Selected</option>
                    </select>
                    <button onClick={applyBulk} disabled={!bulkAction} style={{ padding:'6px 14px', background: bulkAction ? '#003366' : '#ccc', color:'white', border:'none', borderRadius:6, fontSize:12, cursor: bulkAction ? 'pointer' : 'default', fontWeight:700 }}>Apply</button>
                  </>
                )}
                <div style={{ flex:1 }}/>
                <button onClick={() => setActiveTab('upload')} style={{ background:'#003366', color:'white', border:'none', borderRadius:8, padding:'8px 18px', fontWeight:800, fontSize:12, cursor:'pointer' }}>+ Upload Images</button>
              </div>

              <div style={{ display:'flex', gap:6, marginBottom:16, flexWrap:'wrap' }}>
                <button onClick={() => setFilterCat('all')} style={{ padding:'5px 14px', border: filterCat==='all' ? '2px solid #003366' : '1.5px solid #e0e7f0', borderRadius:20, background: filterCat==='all' ? '#003366' : 'white', color: filterCat==='all' ? 'white' : '#666', fontWeight:700, fontSize:11, cursor:'pointer' }}>All ({items.length})</button>
                {catCounts.map(c => (
                  <button key={c.id} onClick={() => setFilterCat(c.id===filterCat ? 'all' : c.id)} style={{ padding:'5px 14px', border: filterCat===c.id ? '2px solid #003366' : '1.5px solid #e0e7f0', borderRadius:20, background: filterCat===c.id ? '#e8f0fe' : 'white', color: filterCat===c.id ? '#003366' : '#666', fontWeight:700, fontSize:11, cursor:'pointer' }}>
                    {c.icon} {c.label} ({c.count})
                  </button>
                ))}
              </div>

              {sorted.length === 0 ? (
                <div style={{ textAlign:'center', padding:'60px 0', color:'#aaa' }}>
                  <div style={{ fontSize:48, marginBottom:12 }}>🔍</div>
                  <div style={{ fontWeight:700, fontSize:16 }}>No images match this filter</div>
                  <button onClick={() => { setSearch(''); setFilterCat('all'); setFilterModule('all'); setFilterDomain('all') }}
                    style={{ marginTop:12, padding:'7px 18px', background:'#e8f0fe', color:'#003366', border:'none', borderRadius:8, fontWeight:700, fontSize:12, cursor:'pointer' }}>
                    Clear filters
                  </button>
                </div>
              ) : viewMode === 'grid' ? (
                <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(148px,1fr))', gap:12 }}>
                  {sorted.map(item => {
                const isOwn = item.uploadedBy && user?._id && String(item.uploadedBy) === String(user._id)
                return <ImageCard key={item.id} item={item} selected={selected.has(item.id)}
                  onSelect={() => toggleSelect(item.id)}
                  onEdit={() => isOwn && setEditItem(item)}
                  onView={() => setLightbox(item)}
                  viewMode="grid" activeLanguage={activeLang} isOwn={isOwn} taxonomy={taxonomy}/>
              })}
                </div>
              ) : (
                <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                  {sorted.map(item => {
                const isOwn = item.uploadedBy && user?._id && String(item.uploadedBy) === String(user._id)
                return <ImageCard key={item.id} item={item} selected={selected.has(item.id)}
                  onSelect={() => toggleSelect(item.id)}
                  onEdit={() => isOwn && setEditItem(item)}
                  onView={() => setLightbox(item)}
                  viewMode="list" activeLanguage={activeLang} isOwn={isOwn} taxonomy={taxonomy}/>
              })}
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* UPLOAD */}
      {activeTab === 'upload' && (
        <div style={{ padding:24, maxWidth:700, margin:'0 auto' }}>
          <div style={{ fontWeight:800, fontSize:20, color:'#003366', marginBottom:6 }}>📤 Upload Images</div>
          <UploadZone getAuthHeader={getAuthHeader} onUpload={addItem} onGoToLibrary={() => setActiveTab('library')}/>
          <div style={{ background:'white', borderRadius:12, padding:20, boxShadow:'0 1px 4px rgba(0,0,0,.06)' }}>
            <div style={{ fontWeight:700, fontSize:13, color:'#003366', marginBottom:12 }}>📋 Upload Guidelines</div>
            {[['✅','Supported: PNG, JPG, WebP, SVG'],['✅','Recommended: square images, min 300×300px'],['✅','One subject per image — clear, uncluttered'],['✅','After upload, add multilingual labels in Library'],['⚠️','Avoid images with text overlaid'],['⚠️','Check copyright — use CC0 or original images']].map(([ic,t]) => (
              <div key={t} style={{ display:'flex', gap:10, fontSize:13, color:'#555', marginBottom:6 }}><span>{ic}</span><span>{t}</span></div>
            ))}
          </div>
        </div>
      )}

      {/* STATS */}
      {activeTab === 'stats' && (
        <div style={{ padding:24, maxWidth:900, margin:'0 auto' }}>
          <div style={{ fontWeight:800, fontSize:20, color:'#003366', marginBottom:20 }}>📊 Collection Stats</div>
          {loading ? (
            <div style={{ textAlign:'center', padding:'40px 0', color:'#aaa' }}>⏳ Loading…</div>
          ) : items.length === 0 ? (
            <div style={{ textAlign:'center', padding:'60px 0', color:'#aaa' }}>
              <div style={{ fontSize:32, marginBottom:8 }}>📊</div>
              <div style={{ fontWeight:700 }}>No images yet — upload images to see stats</div>
              <button onClick={() => setActiveTab('upload')} style={{ marginTop:12, padding:'8px 20px', background:'#003366', color:'white', border:'none', borderRadius:8, fontWeight:700, fontSize:12, cursor:'pointer' }}>📤 Upload Images</button>
            </div>
          ) : (
            <>
              <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:16, marginBottom:24 }}>
                {[
                  { label:'Total Images', val:items.length,                                      icon:'🖼️', color:'#003366' },
                  { label:'Categories',   val:catCounts.length,                                  icon:'🗂️', color:'#1B8A5A' },
                  { label:'Uploaded',     val:items.length,                                      icon:'📤', color:'#7B2D8B' },
                  { label:'In Modules',   val:items.filter(it => it.modules?.length > 0).length, icon:'🔗', color:'#C0392B' },
                ].map(s => (
                  <div key={s.label} style={{ background:'white', borderRadius:12, padding:20, textAlign:'center', boxShadow:'0 1px 4px rgba(0,0,0,.06)' }}>
                    <div style={{ fontSize:28, marginBottom:6 }}>{s.icon}</div>
                    <div style={{ fontSize:30, fontWeight:900, color:s.color }}>{s.val}</div>
                    <div style={{ fontSize:11, color:'#888', marginTop:2 }}>{s.label}</div>
                  </div>
                ))}
              </div>
              <div style={{ background:'white', borderRadius:12, padding:20, boxShadow:'0 1px 4px rgba(0,0,0,.06)', marginBottom:24 }}>
                <div style={{ fontWeight:700, fontSize:13, color:'#003366', marginBottom:16 }}>Images by Category</div>
                {catCounts.map(c => {
                  const pct = Math.round((c.count / items.length) * 100)
                  return (
                    <div key={c.id} style={{ marginBottom:10 }}>
                      <div style={{ display:'flex', justifyContent:'space-between', marginBottom:4 }}>
                        <span style={{ fontSize:12, fontWeight:700 }}>{c.icon} {c.label}</span>
                        <span style={{ fontSize:12, color:'#888' }}>{c.count} · {pct}%</span>
                      </div>
                      <div style={{ height:8, background:'#f0f4f8', borderRadius:6, overflow:'hidden' }}>
                        <div style={{ width:`${pct}%`, height:'100%', background:'#003366', borderRadius:6 }}/>
                      </div>
                    </div>
                  )
                })}
              </div>

              {/* ── NCF Indicator Coverage — content-creation gap analysis ── */}
              {taxonomy && (
                <div style={{ background:'white', borderRadius:12, padding:20, boxShadow:'0 1px 4px rgba(0,0,0,.06)' }}>
                  <div style={{ display:'flex', alignItems:'baseline', justifyContent:'space-between', marginBottom:4 }}>
                    <div style={{ fontWeight:700, fontSize:13, color:'#003366' }}>🎯 Coverage by Development Indicator (NCF)</div>
                    {untaggedCount > 0 && (
                      <span style={{ fontSize:11, color:'#C0392B', fontWeight:700 }}>{untaggedCount} image{untaggedCount!==1?'s':''} untagged</span>
                    )}
                  </div>
                  <div style={{ fontSize:11, color:'#aaa', marginBottom:16 }}>
                    Indicators with 0 images are gaps — prioritise tagging or uploading content for these.
                  </div>
                  {taxonomy.domains.map(dom => {
                    const inds = indicatorCoverage.filter(i => i.domainId === dom.id)
                    return (
                      <div key={dom.id} style={{ marginBottom:14 }}>
                        <div style={{ fontSize:11, fontWeight:800, color:'#555', marginBottom:6, display:'flex', alignItems:'center', gap:6 }}>
                          <span>{dom.icon}</span><span>{dom.label}</span>
                        </div>
                        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(190px,1fr))', gap:6 }}>
                          {inds.map(ind => (
                            <div key={ind.id}
                              style={{ padding:'7px 10px', borderRadius:8,
                                       background: ind.imageCount === 0 ? '#FDEDEC' : '#F0FFF4',
                                       border:`1px solid ${ind.imageCount === 0 ? '#F5C6C6' : '#A8E6C0'}` }}>
                              <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:3 }}>
                                <span style={{ fontSize:9, fontWeight:800, letterSpacing:.4, color:'#888' }}>{ind.id.toUpperCase()}</span>
                                <span style={{ fontSize:13, fontWeight:800, color: ind.imageCount === 0 ? '#C0392B' : '#1B6B45' }}>{ind.imageCount}</span>
                              </div>
                              <div style={{ fontSize:11, fontWeight:600, color:'#333', lineHeight:1.3 }}>{ind.label}</div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}
            </>
          )}
        </div>
      )}

      {editItem && (() => {
        const isOwn = editItem.uploadedBy && user?._id && String(editItem.uploadedBy) === String(user._id)
        if (!isOwn) { setEditItem(null); return null }
        return <EditModal item={editItem} taxonomy={taxonomy} onSave={async upd => { await saveItem(upd); setEditItem(null) }} onClose={() => setEditItem(null)} onDelete={deleteItem}/>
      })()}
      {lightbox && (
        <Lightbox item={lightbox}
          onClose={() => setLightbox(null)}
          onEdit={() => {
            const isOwn = lightbox.uploadedBy && user?._id && String(lightbox.uploadedBy) === String(user._id)
            if (isOwn) { setEditItem(lightbox); setLightbox(null) }
          }}
          isOwn={lightbox.uploadedBy && user?._id && String(lightbox.uploadedBy) === String(user._id)}
          activeLanguage={activeLang}
          taxonomy={taxonomy}
        />
      )}
      {toast && (
        <div style={{ position:'fixed', bottom:28, left:'50%', transform:'translateX(-50%)',
          background: toast.type === 'ok' ? '#1B6B45' : '#C0392B',
          color:'white', padding:'11px 24px', borderRadius:10, fontWeight:700, fontSize:13,
          zIndex:9999, boxShadow:'0 4px 24px rgba(0,0,0,.22)', whiteSpace:'nowrap' }}>
          {toast.msg}
        </div>
      )}
      <div style={{ height:52 }}/>
    </div>
  )
}